import { useEffect, useState } from 'react';
import { useSubmissions } from '@/hooks/useSubmissions';
import type { Denunciation } from '@/types';

export default function AdminDenuncias() {
  const { denunciations, counts, updateDenunciationStatus } = useSubmissions();
  const [search, setSearch] = useState('');
  const [identificarFilter, setIdentificarFilter] = useState<string>('Todos');
  const [selected, setSelected] = useState<Denunciation | null>(null);
  const [statusDraft, setStatusDraft] = useState<Denunciation['status']>('Em análise');
  const [reasonDraft, setReasonDraft] = useState('');

  useEffect(() => {
    if (selected) {
      setStatusDraft(selected.status);
      setReasonDraft(selected.statusReason || '');
    }
  }, [selected]);

  const filtered = denunciations.filter((r) => {
    const matchesSearch =
      (r.displayId || r.id).toLowerCase().includes(search.toLowerCase()) ||
      (r.nomeIdentificado || '').toLowerCase().includes(search.toLowerCase()) ||
      r.setor.toLowerCase().includes(search.toLowerCase()) ||
      r.tipoAssedio.toLowerCase().includes(search.toLowerCase()) ||
      r.detalhes.toLowerCase().includes(search.toLowerCase()) ||
      r.autoresNome.toLowerCase().includes(search.toLowerCase());
    const matchesIdentificar =
      identificarFilter === 'Todos' ||
      (identificarFilter === 'Identificada' && r.identificar) ||
      (identificarFilter === 'Anônima' && !r.identificar);
    return matchesSearch && matchesIdentificar;
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-portal-gray-900">Denúncias Recebidas</h1>
        <p className="text-sm text-portal-gray-500">
          Gerencie as denúncias recebidas pelo canal de denúncias.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', value: counts.denunciation, color: 'bg-portal-purple text-white' },
          { label: 'Identificadas', value: denunciations.filter((d) => d.identificar).length, color: 'bg-portal-purple-100 text-portal-purple' },
          { label: 'Anônimas', value: denunciations.filter((d) => !d.identificar).length, color: 'bg-portal-gray-100 text-portal-gray-700' },
          { label: 'Alta Gravidade (7+)', value: denunciations.filter((d) => d.nivelAssedio >= 7).length, color: 'bg-red-100 text-red-700' },
        ].map((s) => (
          <div key={s.label} className={`${s.color} rounded-xl p-4`}>
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs font-medium opacity-90">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-portal-gray-200 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-portal-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, setor, tipo ou autores..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-portal-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple"
              />
            </div>
          </div>
          <select
            value={identificarFilter}
            onChange={(e) => setIdentificarFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-portal-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple cursor-pointer"
          >
            <option>Todos</option>
            <option>Identificada</option>
            <option>Anônima</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-portal-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-portal-gray-100 flex items-center justify-between">
          <span className="text-sm font-medium text-portal-gray-700">
            Denúncias ({filtered.length})
          </span>
        </div>
        {filtered.length === 0 ?(
          <div className="p-12 text-center">
            <i className="ri-inbox-line text-portal-gray-300 text-3xl mb-3 block"></i>
            <p className="text-sm text-portal-gray-500">Nenhuma denúncia encontrada.</p>
          </div>
        ) : (
          <div className="divide-y divide-portal-gray-100">
            {filtered.map((r) => (
              <div key={r.id} className="px-5 py-4 hover:bg-portal-gray-50/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-portal-gray-500">
                        {r.displayId || r.id}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        r.identificar ?'bg-portal-purple-100 text-portal-purple' : 'bg-portal-gray-100 text-portal-gray-600'
                      }`}>
                        {r.identificar ?'Identificada' : 'Anônima'}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        r.nivelAssedio >= 7 ?'bg-red-100 text-red-700' :
                        r.nivelAssedio >= 4 ?'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        Nível {r.nivelAssedio}/9
                      </span>
                      <span className="text-xs text-portal-gray-400">
                        {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-portal-gray-800 mb-1">
                      {r.identificar ?r.nomeIdentificado : 'Denúncia Anônima'} — {r.setor}
                    </p>
                    <p className="text-sm text-portal-gray-600 line-clamp-2">{r.detalhes}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-portal-gray-400">
                      <span>Tipo: {r.tipoAssedio}</span>
                      <span>Autor: {r.autoresNome}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelected(r)}
                    className="flex-shrink-0 text-portal-purple hover:text-portal-purple-dark text-xs font-medium cursor-pointer whitespace-nowrap"
                  >
                    Ver detalhes
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelected(null)}></div>
          <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="px-6 py-5 border-b border-portal-gray-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-portal-gray-800">Detalhes da Denúncia</h3>
              <button
                onClick={() => setSelected(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-portal-gray-400 hover:text-portal-gray-600 hover:bg-portal-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">ID</p>
                  <p className="text-sm font-mono text-portal-gray-700">{selected.displayId || selected.id}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Identificação</p>
                  <p className="text-sm font-medium text-portal-gray-800">
                    {selected.identificar ?selected.nomeIdentificado : 'Anônima'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Setor</p>
                  <p className="text-sm font-medium text-portal-gray-800">{selected.setor}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Tipo de Assédio</p>
                  <p className="text-sm font-medium text-red-700">{selected.tipoAssedio}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Autor(es)</p>
                  <p className="text-sm text-portal-gray-700">{selected.autoresNome}</p>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Nível</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 bg-portal-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          selected.nivelAssedio >= 7 ?'bg-red-500' :
                          selected.nivelAssedio >= 4 ?'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${(selected.nivelAssedio / 9) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-portal-gray-700">{selected.nivelAssedio}/9</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Última Ocorrência</p>
                  <p className="text-sm text-portal-gray-700">{selected.ultimaOcorrencia}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-portal-gray-400 mb-1">Detalhes</p>
                <p className="text-sm text-portal-gray-700 bg-portal-gray-50 rounded-lg p-3">{selected.detalhes}</p>
              </div>
              {selected.observacoes && (
                <div>
                  <p className="text-xs text-portal-gray-400 mb-1">Observações</p>
                  <p className="text-sm text-portal-gray-700 bg-portal-gray-50 rounded-lg p-3">{selected.observacoes}</p>
                </div>
              )}
              {selected.identificar && selected.acessoRetorno && (
                <div className="flex items-center gap-2 text-xs text-portal-gray-500">
                  <i className="ri-mail-line"></i>
                  Contato: {selected.acessoRetorno}
                </div>
              )}
              <div className="rounded-xl border border-portal-gray-200 p-4 space-y-3">
                <div>
                  <label className="text-xs text-portal-gray-500 mb-1 block">Alterar status</label>
                  <select
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value as Denunciation['status'])}
                    className="w-full text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20"
                  >
                    <option value="Em análise">Em análise</option>
                    <option value="Concluído">Concluído</option>
                  </select>
                </div>
                {statusDraft === 'Concluído' && (
                  <textarea
                    value={reasonDraft}
                    onChange={(e) => setReasonDraft(e.target.value)}
                    placeholder="Informe a justificativa/conclusão"
                    className="w-full min-h-24 text-sm px-3 py-2 rounded-lg border border-portal-gray-200 bg-white text-portal-gray-700 focus:outline-none focus:ring-2 focus:ring-portal-purple/20"
                  />
                )}
                <button
                  type="button"
                  onClick={async () => {
                    if (statusDraft === 'Concluído' && !reasonDraft.trim()) return;
                    try {
                      await updateDenunciationStatus(selected.id, statusDraft, reasonDraft.trim());
                      setSelected(null);
                    } catch (err) {
                      alert(err instanceof Error ? err.message : 'Não foi possível salvar o status.');
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-portal-purple rounded-lg hover:bg-portal-purple-dark disabled:opacity-50"
                  disabled={statusDraft === 'Concluído' && !reasonDraft.trim()}
                >
                  Salvar status
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-portal-gray-100 flex justify-end">
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 text-sm font-medium text-portal-gray-600 bg-white border border-portal-gray-200 rounded-lg hover:bg-portal-gray-50 cursor-pointer whitespace-nowrap"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
